/**
 * Đổi mật khẩu bằng mã OTP gửi qua email — dùng chung khung AuthShell với đăng
 * nhập/đăng ký.
 *
 * MỘT MÀN, HAI LỐI VÀO:
 *   - "Quên mật khẩu?" ở trang đăng nhập: chưa đăng nhập, phải tự gõ email;
 *   - "Đổi mật khẩu" ở trang Tài khoản: đã đăng nhập, email điền sẵn.
 *
 * Việc làm y hệt nhau (gõ mã rồi đặt mật khẩu mới) nên không tách thành hai màn,
 * nhưng CHỮ phải khác: người đang đăng nhập bấm "Đổi mật khẩu" mà rơi vào một
 * màn ghi "Khôi phục mật khẩu" thì tưởng mình bấm nhầm. Trang Tài khoản truyền
 * cờ `doiMatKhau` để màn này biết mình đang ở vai nào.
 *
 * Hai bước trên cùng một màn:
 *   1. nhập email đã đăng ký  -> POST /auth/password/forgot gửi mã 6 số;
 *   2. nhập mã + mật khẩu mới -> POST /auth/password/reset.
 *
 * Email chứ không phải số điện thoại, vì mã đi bằng email. Đó cũng là lý do màn
 * đăng ký giờ bắt buộc nhập email: không có email là không khôi phục được.
 *
 * Bỏ tab đăng nhập/đăng ký vì đây không phải một lựa chọn ngang hàng với hai
 * màn đó.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import toast from "react-hot-toast";

import AuthShell from "../../components/ui/AuthShell";
import FieldInput from "../../components/form/FieldInput";
import TIcon from "../../components/ui/TIcon";
import * as SBService from "../../helpers/sportbookService";
import { apiErrorMessage } from "../../helpers/apiError";
import { loaderState } from "../../states/common";

const EMAIL_HOP_LE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Hai lần xin mã phải cách nhau bấy nhiêu giây — khớp với chặn phía máy chủ. */
const CHO_GIAY = 60;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setLoader] = useRecoilState(loaderState);
  /** 'email' = đang xin mã; 'otp' = mã đã gửi, chờ nhập mã và mật khẩu mới. */
  const [buoc, setBuoc] = useState<"email" | "otp">("email");
  const [errorMessage, setErrorMessage] = useState("");
  /*
   * Đếm ngược tự giữ ở màn này, vì máy chủ KHÔNG còn báo 429 nữa: chỉ email có
   * tài khoản mới chạm tới chặn đó, nên một mã lỗi riêng ở đó chính là câu trả
   * lời cho "email này có tài khoản không". Máy chủ giờ lặng lẽ bỏ qua, nên nếu
   * màn hình không tự đếm thì người bấm lại sớm sẽ thấy "đã gửi" rồi ngồi chờ
   * một lá thư không bao giờ tới.
   */
  const [choGiay, setChoGiay] = useState(0);
  const vaoTu = (location.state ?? null) as { email?: string; doiMatKhau?: boolean } | null;
  /** Vào từ trang Tài khoản (đã đăng nhập) chứ không phải từ "Quên mật khẩu?". */
  const doiMatKhau = Boolean(vaoTu?.doiMatKhau);
  const [formData, setFormData] = useState({
    /* Vào từ "Đổi mật khẩu" trong app thì đã biết email của họ - điền sẵn thay
       vì bắt gõ lại một thứ hệ thống đang giữ. Vẫn sửa được: màn này cũng là màn
       người chưa đăng nhập bấm "Quên mật khẩu?" từ trang đăng nhập, lúc đó state
       rỗng và ô trống như cũ. */
    email: vaoTu?.email ?? "",
    otp: "",
    password: "",
    confirm_password: "",
  });
  const [formError, setFormError] = useState({
    email: "",
    otp: "",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    document.title = doiMatKhau ? "Biadi — Đổi mật khẩu" : "Biadi — Khôi phục mật khẩu";
  }, [doiMatKhau]);

  useEffect(() => {
    if (choGiay <= 0) {
      return;
    }
    const dongHo = setTimeout(() => setChoGiay((n) => n - 1), 1000);

    return () => clearTimeout(dongHo);
  }, [choGiay]);

  const changeInput = (name: string, event: any) => {
    setFormData((f) => ({ ...f, [name]: event.target.value }));
    setFormError((e) => ({ ...e, [name]: "" }));
    setErrorMessage("");
  };

  /** Bước 1: xin mã. */
  const xinMa = async () => {
    const email = formData.email.trim();
    if (!email) {
      setFormError((e) => ({ ...e, email: "Bắt buộc" }));
      return;
    }
    if (!EMAIL_HOP_LE.test(email)) {
      setFormError((e) => ({ ...e, email: "Định dạng email không đúng" }));
      return;
    }

    if (choGiay > 0) {
      setErrorMessage(`Vui lòng đợi ${choGiay} giây trước khi xin mã mới.`);

      return;
    }

    try {
      setLoader(true);
      await SBService.requestPasswordOtp(email);
      setLoader(false);
      setChoGiay(CHO_GIAY);
      // Vào bước 2 chứ không chỉ hiện toast: người dùng cần một ô để gõ mã, và
      // bắt họ tự tìm đường quay lại màn này sau khi mở hộp thư là mất luồng.
      setBuoc("otp");
      /* "Nếu email này có tài khoản" chứ không phải "đã gửi tới email của bạn":
         máy chủ cố ý trả lời giống hệt nhau cho email có và không có tài khoản,
         nên hứa chắc là hứa sai với một nửa số trường hợp. */
      toast.success("Nếu email này có tài khoản, mã xác nhận đã được gửi");
    } catch (err: any) {
      setLoader(false);
      setErrorMessage(apiErrorMessage(err));
    }
  };

  /** Bước 2: mã + mật khẩu mới. */
  const datLaiMatKhau = async () => {
    const nextError = { email: "", otp: "", password: "", confirm_password: "" };
    if (!formData.otp.trim()) {
      nextError.otp = "Bắt buộc";
    } else if (!/^\d{6}$/.test(formData.otp.trim())) {
      nextError.otp = "Mã gồm 6 chữ số";
    }
    if (!formData.password) {
      nextError.password = "Bắt buộc";
    } else if (formData.password.length < 6) {
      nextError.password = "Mật khẩu không được ít hơn 6 ký tự";
    }
    if (!formData.confirm_password) {
      nextError.confirm_password = "Bắt buộc";
    } else if (formData.confirm_password !== formData.password) {
      nextError.confirm_password = "Không khớp với Mật khẩu đã nhập";
    }
    setFormError(nextError);
    if (nextError.otp || nextError.password || nextError.confirm_password) {
      return;
    }

    try {
      setLoader(true);
      await SBService.resetPasswordWithOtp({
        email: formData.email.trim(),
        otp: formData.otp.trim(),
        password: formData.password,
      });
      setLoader(false);
      /* Máy chủ đã xoá mọi phiên cũ khi đổi mật khẩu, nên dọn luôn token trên
         máy này: còn giữ lại thì màn hình trong app sẽ gọi API bằng một token
         đã chết và người dùng gặp lỗi 401 rải rác. */
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userInfo");
      toast.success("Đã đổi mật khẩu. Mọi thiết bị đã đăng xuất, vui lòng đăng nhập lại.");
      navigate("/auth/login");
    } catch (err: any) {
      setLoader(false);
      setErrorMessage(apiErrorMessage(err));
    }
  };

  const submit = (e: any) => {
    e.preventDefault();
    setErrorMessage("");
    if (buoc === "email") {
      void xinMa();
      return;
    }
    void datLaiMatKhau();
  };

  return (
    <AuthShell
      showTabs={false}
      title={doiMatKhau ? "ĐỔI MẬT KHẨU" : "KHÔI PHỤC MẬT KHẨU"}
      subtitle={
        buoc === "otp"
          ? `Nếu ${formData.email.trim()} có tài khoản, mã xác nhận đã được gửi tới đó. Mã có hiệu lực 15 phút.`
          : doiMatKhau
          ? "Để đổi mật khẩu, chúng tôi gửi một mã xác nhận tới email của bạn. Có mã rồi mới đặt được mật khẩu mới."
          : "Nhập email đã đăng ký, chúng tôi sẽ gửi mã xác nhận để đặt lại mật khẩu."
      }
    >
      <form className="auth-form" onSubmit={submit}>
        <FieldInput
          label={doiMatKhau ? "EMAIL NHẬN MÃ XÁC NHẬN" : "EMAIL ĐÃ ĐĂNG KÝ"}
          name="email"
          type="email"
          icon="mail"
          placeholder="ban@email.com"
          required
          value={formData.email}
          error={formError.email}
          autoComplete="username"
          /* Sang bước 2 thì khoá lại: mã đã gửi cho đúng email này, sửa đi là
             mã không khớp và máy chủ trả "mã không đúng" mà không rõ vì sao. */
          disabled={buoc === "otp"}
          hint={
            /* Tài khoản chưa có email thì luồng này vô dụng với họ: máy chủ tra
               theo email, không có email là không bao giờ nhận được mã. Nói ra ở
               đây, chứ để họ gõ đại một email rồi ngồi chờ thư thì tệ hơn. */
            doiMatKhau && buoc === "email" && !vaoTu?.email
              ? "Tài khoản của bạn chưa có email. Thêm email ở mục Thông tin cá nhân trước, rồi quay lại đây."
              : undefined
          }
          onChange={changeInput}
        />

        {buoc === "otp" ? (
          <>
            <FieldInput
              label="MÃ XÁC NHẬN"
              name="otp"
              type="number"
              icon="shield-lock"
              placeholder="123456"
              required
              value={formData.otp}
              error={formError.otp}
              autoComplete="one-time-code"
              onChange={changeInput}
            />
            <FieldInput
              label="MẬT KHẨU MỚI"
              name="password"
              type="password"
              icon="lock"
              placeholder="••••••••"
              required
              value={formData.password}
              error={formError.password}
              autoComplete="new-password"
              onChange={changeInput}
            />
            <FieldInput
              label="XÁC NHẬN MẬT KHẨU MỚI"
              name="confirm_password"
              type="password"
              icon="lock"
              placeholder="••••••••"
              required
              value={formData.confirm_password}
              error={formError.confirm_password}
              autoComplete="new-password"
              onChange={changeInput}
            />
          </>
        ) : null}

        {errorMessage ? (
          <span className="field-error" style={{ whiteSpace: "pre-line" }}>
            <TIcon name="alert-circle" />
            {errorMessage}
          </span>
        ) : null}

        <button type="submit" className="btn btn-primary" style={{ marginTop: 4 }}>
          {buoc === "email" ? "GỬI MÃ XÁC NHẬN" : "ĐỔI MẬT KHẨU"}
        </button>

        {buoc === "otp" ? (
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              setBuoc("email");
              setFormError({ email: "", otp: "", password: "", confirm_password: "" });
              setErrorMessage("");
            }}
          >
            GỬI LẠI MÃ / ĐỔI EMAIL
            {choGiay > 0 ? ` (${choGiay}s)` : ""}
          </button>
        ) : null}
      </form>

      {doiMatKhau ? (
        <p className="auth-foot">
          {/* Đang đăng nhập thì "Quay lại đăng nhập" là một lối ra vô nghĩa. */}
          <Link to="/profile">Quay lại trang Tài khoản</Link>
        </p>
      ) : (
        <p className="auth-foot">
          Nhớ ra mật khẩu rồi? <Link to="/auth/login">Quay lại đăng nhập</Link>
        </p>
      )}
    </AuthShell>
  );
};

export default ForgotPasswordPage;
